import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, Sparkles, Globe, PenTool, Palette, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { TextField, Section, StringList, PageHeader } from "./AdminUI";

const ICON_OPTIONS = [
  { key: "sparkles", icon: Sparkles, label: "Sparkles" },
  { key: "globe", icon: Globe, label: "Globe" },
  { key: "pen", icon: PenTool, label: "Pen" },
  { key: "palette", icon: Palette, label: "Palette" },
];

const empty = { title: "", desc: "", bullets: [], icon_key: "sparkles" };

const ServicesAdmin = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await api.listServices());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateLocal = (id, patch) =>
    setItems((arr) => arr.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  const onSave = async (item) => {
    setSavingId(item.id);
    try {
      const saved = await api.updateService(item.id, item);
      updateLocal(item.id, saved);
      toast({ title: "Saved", description: `"${item.title}" updated.` });
    } catch (e) {
      toast({ title: "Couldn't save", description: e.message });
    } finally {
      setSavingId(null);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try {
      await api.deleteService(id);
      setItems((arr) => arr.filter((i) => i.id !== id));
      toast({ title: "Deleted" });
    } catch (e) {
      toast({ title: "Couldn't delete", description: e.message });
    }
  };

  const onAdd = async () => {
    try {
      const created = await api.createService({
        ...empty,
        title: "New service",
        desc: "Describe what you offer.",
        bullets: ["Bullet one", "Bullet two"],
        order: items.length + 1,
      });
      setItems((arr) => [...arr, created]);
    } catch (e) {
      toast({ title: "Couldn't add", description: e.message });
    }
  };

  if (loading) {
    return (
      <div className="py-20 text-center">
        <Loader2 className="w-6 h-6 animate-spin inline-block text-[color:var(--muted)]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        label="Services"
        title="What you offer clients"
        action={
          <button onClick={onAdd} className="mk-btn mk-btn-primary">
            <Plus className="w-4 h-4" /> Add service
          </button>
        }
      />

      {items.length === 0 && (
        <div className="py-16 text-center text-[color:var(--muted)] border border-dashed border-black/15 rounded-2xl">
          No services yet. Click “Add service” to create one.
        </div>
      )}

      {items.map((item) => (
        <Section key={item.id} title={item.title || "Untitled service"} hint={`Order ${item.order ?? "—"}`}>
          <div className="grid md:grid-cols-2 gap-5">
            <TextField label="Title" value={item.title} onChange={(v) => updateLocal(item.id, { title: v })} />
            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-[color:var(--muted)]">Icon</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {ICON_OPTIONS.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => updateLocal(item.id, { icon_key: o.key })}
                    className={`px-3 py-2 rounded-full border text-sm inline-flex items-center gap-2 transition-colors ${
                      item.icon_key === o.key
                        ? "bg-[color:var(--ink)] text-[color:var(--bg)] border-[color:var(--ink)]"
                        : "border-black/20 hover:border-black/50"
                    }`}
                  >
                    <o.icon className="w-4 h-4" /> {o.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <TextField label="Description" textarea rows={3} value={item.desc} onChange={(v) => updateLocal(item.id, { desc: v })} />
          <StringList label="Bullets" items={item.bullets || []} onChange={(v) => updateLocal(item.id, { bullets: v })} placeholder="e.g. Monthly content kits" />
          <div className="grid md:grid-cols-2 gap-5">
            <TextField label="Order" type="number" value={item.order} onChange={(v) => updateLocal(item.id, { order: Number(v) || 0 })} />
          </div>
          <div className="flex justify-between pt-2">
            <button type="button" onClick={() => onDelete(item.id)} className="inline-flex items-center gap-2 text-sm text-[color:var(--coral)] hover:underline">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
            <button type="button" onClick={() => onSave(item)} disabled={savingId === item.id} className="mk-btn mk-btn-primary disabled:opacity-60">
              <Save className="w-4 h-4" /> {savingId === item.id ? "Saving…" : "Save"}
            </button>
          </div>
        </Section>
      ))}
    </div>
  );
};

export default ServicesAdmin;
